import { useEffect, useMemo } from "react";
import { useInfiniteQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ListingV2Response, CategoryCode, ListingStatus } from "@/types/listings-v2";

export interface ListingsFilterParams {
  country_code?: string | null;
  category_code?: CategoryCode | null;
  make?: string | null;
  model?: string | null;
  price_min?: number | null;
  price_max?: number | null;
  year_min?: number | null;
  year_max?: number | null;
  mileage_min?: number | null;
  mileage_max?: number | null;
  fuel?: string | null;
  transmission?: string | null;
  condition?: string | null;
  sort_by?: 'price' | 'year' | 'mileage' | 'created_at';
  sort_order?: 'asc' | 'desc';
  pageSize?: number;
  page?: number; // For traditional pagination (1-indexed)
  enabled?: boolean;
  user_id?: string | null;
  location?: string | null; // City/region search (diacritic-insensitive)
  seats?: number | null; // Minimum number of seats
  body_types?: string[] | null; // Body type synonyms (e.g. ['break','combi','estate'])
  euro_norm?: string | null; // Minimum Euro emission norm (e.g. 'Euro 4' => 4+)
  environmental_badge?: string | null; // DE Umweltplakette color
  power_min?: number | null; // Engine power (CP/HP) lower bound
  power_max?: number | null; // Engine power (CP/HP) upper bound
  doors?: number | null; // Exact doors count
  drive_type?: string | null; // fwd | rwd | awd
  color?: string | null; // Exact color (case-insensitive)
  condition_technical?: string | null; // excellent | very_good | good | average
  seller_type?: string | null; // dealer | private
  features?: string[] | null; // listing_features keys, AND semantics
}

interface FilteredListingResponse {
  id: string;
  make: string;
  model: string;
  year: number;
  price: number;
  currency: string;
  mileage_value: number | null;
  mileage_unit: string | null;
  condition: string;
  photos: string[] | null;
  location_city: string | null;
  location_country: string;
  location_region: string | null;
  created_at: string;
  promoted: boolean;
  auto_promoted: boolean;
  dealer_verified: boolean;
  category_code: string;
  category_name: string;
  attributes: Record<string, any> | null;
  description_text: string | null;
  trim_level: string | null;
  is_new_listing: boolean;
  total_count: number;
  status: string;
  user_id: string | null;
}

/**
 * Shared infinite-query config for the filtered listings RPC.
 * Exported so route loaders can prefetch the exact same key during SSR,
 * which puts real listings into the server-rendered HTML.
 */
export const paginatedListingsQueryOptions = (params: ListingsFilterParams = {}) => {
  const {
    country_code = null,
    category_code = null,
    make = null,
    model = null,
    price_min = null,
    price_max = null,
    year_min = null,
    year_max = null,
    mileage_min = null,
    mileage_max = null,
    fuel = null,
    transmission = null,
    condition = null,
    sort_by = 'created_at',
    sort_order = 'desc',
    pageSize = 24,
    page, // For traditional pagination
    enabled = true,
    user_id = null,
    location = null,
    seats = null,
    body_types = null,
    euro_norm = null,
    environmental_badge = null,
    power_min = null,
    power_max = null,
    doors = null,
    drive_type = null,
    color = null,
    condition_technical = null,
    seller_type = null,
    features = null,
  } = params;

  // Create stable query key from all filter params
  const queryKey = [
    'filtered-listings',
    country_code,
    category_code,
    make,
    model,
    price_min,
    price_max,
    year_min,
    year_max,
    mileage_min,
    mileage_max,
    fuel,
    transmission,
    condition,
    sort_by,
    sort_order,
    pageSize,
    page, // Include page in query key for traditional pagination
    user_id,
    location,
    seats,
    body_types,
    euro_norm,
    environmental_badge,
    power_min,
    power_max,
    doors,
    drive_type,
    color,
    condition_technical,
    seller_type,
    features,
  ];

  return {
    queryKey,

    queryFn: async ({ pageParam = 0 }) => {
      // Use page parameter if provided (traditional pagination), otherwise use pageParam (infinite scroll)
      const offset = page !== undefined ? (page - 1) * pageSize : pageParam * pageSize;
      
      const { data, error } = await (supabase.rpc as any)('get_listings_v2_filtered', {
        p_country_code: country_code || null,
        p_category_code: category_code || null,
        p_make: make || null,
        p_model: model || null,
        p_price_min: price_min || null,
        p_price_max: price_max || null,
        p_year_min: year_min || null,
        p_year_max: year_max || null,
        p_mileage_min: mileage_min || null,
        p_mileage_max: mileage_max || null,
        p_fuel: fuel || null,
        p_transmission: transmission || null,
        p_condition: condition || null,
        p_sort_by: sort_by,
        p_sort_order: sort_order,
        p_limit: pageSize,
        p_offset: offset,
        p_user_id: user_id || null,
        p_location: location || null,
        p_seats: seats || null,
        p_body_types: body_types && body_types.length ? body_types : null,
        p_euro_norm: euro_norm || null,
        p_environmental_badge: environmental_badge || null,
        p_power_min: power_min || null,
        p_power_max: power_max || null,
        p_doors: doors || null,
        p_drive_type: drive_type || null,
        p_color: color || null,
        p_condition_technical: condition_technical || null,
        p_seller_type: seller_type || null,
        p_features: features && features.length ? features : null,
      });

      if (error) {
        console.error('Error fetching filtered listings:', error);
        throw error;
      }

      const listings = (data || []) as FilteredListingResponse[];
      const totalCount = listings[0]?.total_count || 0;

      // Transform to ListingV2Response format
      const transformed: ListingV2Response[] = listings.map((item) => ({
        id: item.id,
        make: item.make,
        model: item.model,
        year: item.year,
        price: item.price,
        currency: item.currency as any,
        mileage_value: item.mileage_value ?? undefined,
        mileage_unit: item.mileage_unit as any,
        condition: item.condition as any,
        status: item.status as ListingStatus,
        photos: item.photos || [],
        location_city: item.location_city ?? undefined,
        location_country: item.location_country,
        location_region: item.location_region ?? undefined,
        created_at: item.created_at,
        promoted: item.promoted,
        auto_promoted: item.auto_promoted,
        dealer_verified: item.dealer_verified,
        category_code: item.category_code,
        category_name: item.category_name,
        attributes: item.attributes ?? undefined,
        description_text: item.description_text ?? undefined,
        trim_level: item.trim_level ?? undefined,
        is_new_listing: item.is_new_listing,
        has_360: false,
        video_url: undefined,
        user_id: item.user_id ?? undefined,
      }));

      return {
        listings: transformed,
        totalCount,
        nextPage: listings.length === pageSize ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage: { nextPage?: number }) => lastPage.nextPage,
    initialPageParam: 0,
    enabled,
    // Păstrează rezultatele curente în timpul re-sortării/filtrării, ca lista să
    // nu clipească în skeleton (permite și tranziția GSAP Flip a cardurilor).
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  };
};

export interface PaginatedListingsPage {
  listings: ListingV2Response[];
  totalCount: number;
  nextPage?: number;
}

export const usePaginatedListings = (params: ListingsFilterParams = {}) => {
  const queryClient = useQueryClient();
  const query = useInfiniteQuery<PaginatedListingsPage>(paginatedListingsQueryOptions(params) as any);
  const currentPage = params.page;
  const paramsKey = JSON.stringify(params);
  const stableParams = useMemo(() => params, [paramsKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !currentPage || !query.data?.pages[0]) return;

    const totalCount = query.data.pages[0].totalCount;
    const pageSize = stableParams.pageSize ?? 24;
    const totalPages = Math.ceil(totalCount / pageSize);
    const adjacentPages = [currentPage - 1, currentPage + 1].filter(
      (page) => page >= 1 && page <= totalPages,
    );

    adjacentPages.forEach((page) => {
      void queryClient.prefetchInfiniteQuery(
        paginatedListingsQueryOptions({ ...stableParams, page }) as any,
      ).then(() => {
        const cached = queryClient.getQueryData<{ pages?: PaginatedListingsPage[] }>(
          paginatedListingsQueryOptions({ ...stableParams, page }).queryKey,
        );
        cached?.pages?.[0]?.listings.forEach((listing) => {
          const src = listing.photos?.[0];
          if (src) {
            const image = new Image();
            image.decoding = "async";
            image.src = src;
          }
        });
      }).catch(() => undefined);
    });
  }, [currentPage, stableParams, query.data, queryClient]);

  return query;
};



// Hook to get total count efficiently - now uses the same RPC (returns count in first result)
export const useListingsCount = (options: { country_code?: string; category_code?: string } = {}) => {
  const { country_code, category_code } = options;
  
  return useInfiniteQuery({
    queryKey: ['listings-count', country_code, category_code],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_listings_v2_filtered', {
        p_country_code: country_code || undefined,
        p_category_code: category_code || undefined,
        p_limit: 1,
        p_offset: 0,
      });
      
      if (error) {
        console.error('Error fetching count:', error);
        return { count: 0 };
      }
      
      const result = data as FilteredListingResponse[];
      return { count: result[0]?.total_count || 0 };
    },
    getNextPageParam: () => undefined,
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
  });
};
